// Article enrichment — runs at render time on every blog post HTML
// (regardless of how short or long the original content is) to make
// articles feel visually richer without rewriting their copy.
//
// What it does, in order:
//   1. Inserts a contextual section image after the 2nd <h2> and after
//      the 4th <h2> (if the article has that many sections). Images come
//      from the per-category rotation pool in `categoryImages.js`.
//   2. Wraps the FIRST paragraph of every article in a `.lede` class so
//      it can be styled as a larger, lighter intro paragraph.
//   3. Appends a "Key Takeaways" callout box at the end, auto-built from
//      the article's <h2> headings.
//   4. Appends a brand "Apply It Today" CTA pointing to the app.

import { getSectionImage } from './categoryImages';

/**
 * Build a single <figure> tag with caption derived from the heading text
 * that immediately precedes it.
 */
const buildFigure = (src, caption) => {
  if (!src) return '';
  const safeCaption = caption
    ? caption.replace(/<[^>]+>/g, '').replace(/"/g, '&quot;').trim()
    : '';
  return `
    <figure class="article-figure">
      <img src="${src}" alt="${safeCaption}" loading="lazy" width="1200" height="675" />
      ${safeCaption ? `<figcaption>${safeCaption}</figcaption>` : ''}
    </figure>
  `;
};

/**
 * Insert a section image after the Nth <h2> + its first <p>. We splice
 * in *between* sections so the image sits above the next heading and
 * provides a visual breath between content blocks.
 */
const injectImageAfterSection = (html, headingIndex, image, caption) => {
  if (!image) return html;
  // Match each <h2 ...>...</h2> — splitting around them lets us splice
  // an extra block between the (headingIndex+1)th and (headingIndex+2)th
  // heading. We use a simple split because the actual prose between two
  // h2s may contain anything (p, ul, div...).
  const parts = html.split(/(<h2[^>]*>[\s\S]*?<\/h2>)/i);
  // parts looks like: [pre, <h2>1</h2>, body1, <h2>2</h2>, body2, ...]
  // headings sit at odd indexes (1, 3, 5, ...) — body that follows the
  // Nth heading (1-indexed) sits at index 2N.
  const targetBodyIndex = 2 * (headingIndex + 1); // body AFTER heading
  if (targetBodyIndex >= parts.length) return html;
  parts[targetBodyIndex] = `${parts[targetBodyIndex]}\n${buildFigure(image, caption)}\n`;
  return parts.join('');
};

/** Pull every <h2> heading text in order. */
const extractHeadings = (html) => {
  const out = [];
  const re = /<h2[^>]*>([\s\S]*?)<\/h2>/gi;
  let m;
  while ((m = re.exec(html)) !== null) {
    const text = m[1].replace(/<[^>]+>/g, '').trim();
    if (text) out.push(text);
  }
  return out;
};

/** Mark the first <p> in the article body as `.lede` for bigger styling. */
const markLedeParagraph = (html) => {
  return html.replace(
    /<p(\s[^>]*)?>/i,
    (match, attrs = '') => {
      // Preserve any existing class attribute, append `lede`.
      if (attrs && /class\s*=\s*"/.test(attrs)) {
        return match.replace(
          /class\s*=\s*"([^"]*)"/i,
          (_m, cls) => `class="${cls} lede"`
        );
      }
      return `<p${attrs} class="lede">`;
    }
  );
};

/**
 * Build the "Key Takeaways" callout. We use the article's <h2> headings
 * as the bullets — they're already a natural outline. Caps at 6 to keep
 * the box scannable.
 */
const buildKeyTakeaways = (headings) => {
  if (!headings || headings.length < 2) return '';
  const items = headings.slice(0, 6);
  const lis = items.map((h) => `<li>${h}</li>`).join('');
  return `
    <aside class="article-takeaways">
      <p class="article-takeaways__eyebrow">Key takeaways</p>
      <ul>${lis}</ul>
    </aside>
  `;
};

/** Final brand CTA — links into the app. */
const buildApplyCta = () => `
  <aside class="article-apply">
    <p class="article-apply__eyebrow">Apply it today</p>
    <h3>Reading is step one. Practice is step two.</h3>
    <p>
      Levanta turns the ideas in this article into a daily 10-minute
      practice you actually return to. Pick one principle above and try
      it today — inside the system, with structure.
    </p>
    <p>
      <a href="/download-app" class="article-apply__btn">Download Levanta →</a>
    </p>
  </aside>
`;

/**
 * Public entry point — takes raw article HTML + its article object and
 * returns a richer HTML string.
 */
export const enrichArticleHtml = (html, article) => {
  if (!html || !article) return html || '';

  const headings = extractHeadings(html);
  let out = html;

  // Mark first paragraph as the lede.
  out = markLedeParagraph(out);

  // Insert section images after the 2nd and 4th <h2> (if they exist).
  if (headings.length >= 3) {
    out = injectImageAfterSection(
      out,
      1, // 0-indexed → after the 2nd heading
      getSectionImage(article, 0),
      headings[1]
    );
  }
  if (headings.length >= 5) {
    out = injectImageAfterSection(
      out,
      3, // after the 4th heading
      getSectionImage(article, 2),
      headings[3]
    );
  }

  // Append Key Takeaways + Apply CTA.
  out += buildKeyTakeaways(headings);
  out += buildApplyCta();

  return out;
};

export default enrichArticleHtml;
